import { type FileToUpload, useIntl, type UserRequirementDocumentCategory } from '@capsicum/common';
import { useSetAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

import { sourceOfIncomeAtom } from '../../data-access';

export type SOIParsedGroup = {
	description: string;
	name: string;
	numberOfDocuments: number;
	requirementId: string;
};

export const useSOICategoryStep = ({ category }: { category: UserRequirementDocumentCategory }) => {
	const { $t, getLocalizedField } = useIntl();
	const dispatch = useSetAtom(sourceOfIncomeAtom);

	const groups = useMemo(
		() =>
			category.types.reduce<Record<string, Array<SOIParsedGroup>>>((acc, type) => {
				acc[type.grouping] = acc[type.grouping] ?? [];
				acc[type.grouping]?.push({
					description: getLocalizedField('description', type),
					name: getLocalizedField('name', type),
					numberOfDocuments: type.numberOfDocuments,
					requirementId: type.id
				});

				return acc;
			}, {}),
		[]
	);

	const onUploadFiles = useCallback(
		({ files, requirementId }: { files: Array<FileToUpload>; requirementId: string }) => {
			dispatch({
				category: category.id,
				files: files,
				requirement: requirementId,
				type: 'SET_FILES'
			});
		},
		[dispatch]
	);

	const getGroupDescription = (index: number) => {
		if (index === 0) {
			return category.description;
		} else if ((groups[index + 1] as Array<SOIParsedGroup>).length > 1) {
			return $t('sourceOfIncome.category.atLeastOneMore');
		} else {
			return $t('sourceOfIncome.category.nextDocument');
		}
	};

	const hasUniqueType = useMemo(() => {
		const groupKeys = Object.keys(groups);
		const firstGroup = groupKeys[0];

		return Boolean(groupKeys.length === 1 && firstGroup && groups[firstGroup]?.length === 1);
	}, [groups]);

	return { getGroupDescription, groups, hasUniqueType, onUploadFiles };
};
